import React, { useState, useRef, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Box,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  ClickAwayListener,
  Popper,
  Fade,
  Button,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import Iconify from '../iconify';

export function RHFCategorySelect({
  name,
  label,
  size = "small",
  placeholder = "Select or create category",
  helperText,
  categories = [],
  onCreateCategory,
  ...other
}) {
  const { control } = useFormContext();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const anchorRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter categories based on search
  const filteredCategories = categories.filter(category => 
    category.name.toLowerCase().includes(search.toLowerCase())
  );

  // Check if there's already an "Uncategorized" category in the database
  const hasUncategorizedCategory = categories.some(category => 
    category.name.toLowerCase() === 'uncategorized'
  );

  // Check if search term matches existing category
  const exactMatch = categories.find(category => 
    category.name.toLowerCase() === search.toLowerCase()
  );

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 10);
    }
  }, [open]);

  // Handle click away
  const handleClickAway = () => {
    setOpen(false);
    setSearch('');
    setCreateError('');
  };

  // Handle category creation
  const handleCreateCategory = async (field) => {
    if (!search.trim()) return;
    
    setIsCreating(true);
    setCreateError('');
    
    try {
      const newCategory = await onCreateCategory(search.trim());
      if (newCategory && newCategory._id) {
        // Select the newly created category
        field.onChange(newCategory._id);
        setSearch('');
        setOpen(false);
      }
    } catch (error) {
      setCreateError(error.message || 'Failed to create category');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // Find selected category name
        const selectedCategory = categories.find(cat => cat._id === field.value);
        const displayValue = selectedCategory ? selectedCategory.name : 
                           (field.value === 'uncategorized' && !hasUncategorizedCategory) ? 'Uncategorized' : 
                           placeholder;

        return (
          <Box sx={{ width: '100%', m: 0, p: 0 }}>
            {label && (
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500, 
                  mb: 0.5, 
                  color: error ? 'error.main' : 'text.primary' 
                }}
              >
                {label}
              </Typography>
            )}

            {/* Custom select field */}
            <TextField
              inputRef={anchorRef}
              fullWidth
              size={size}
              value={displayValue}
              onClick={() => setOpen(prev => !prev)}
              placeholder={placeholder}
              error={!!error}
              helperText={error ? error.message : helperText}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Box 
                      component="span" 
                      sx={{ 
                        fontSize: '1.25rem', 
                        transition: 'transform 0.2s',
                        transform: open ? 'rotate(180deg)' : 'none'
                      }}
                    >
                      ▾
                    </Box>
                  </InputAdornment>
                ),
                sx: {
                  cursor: 'pointer',
                }
              }}
              sx={{
                m: 0,
                '& .MuiOutlinedInput-root': {
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)',
                  }
                }
              }}
              {...other}
            />

            {/* Dropdown menu */}
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              transition
              style={{ width: Math.max(anchorRef.current?.offsetWidth || 0, 300), zIndex: 1301 }}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={200}>
                  <Paper
                    elevation={8}
                    sx={{
                      mt: 0.5,
                      maxHeight: 300,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <ClickAwayListener onClickAway={handleClickAway}>
                      <Box>
                        {/* Search input */}
                        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                          <TextField
                            ref={searchInputRef}
                            fullWidth
                            size="small"
                            placeholder="Search or type new category name..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Iconify icon="eva:search-fill" width={16} height={16} />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>

                        {/* Error display */}
                        {createError && (
                          <Box sx={{ p: 1 }}>
                            <Alert severity="error" sx={{ py: 0.5 }}>
                              {createError}
                            </Alert>
                          </Box>
                        )}

                        {/* Options list */}
                        <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                          {/* Uncategorized option - only show if no "Uncategorized" category exists in database */}
                          {!hasUncategorizedCategory && (
                            <Box
                              onClick={() => {
                                field.onChange('uncategorized');
                                setOpen(false);
                                setSearch('');
                              }}
                              sx={{
                                px: 2,
                                py: 1.5,
                                cursor: 'pointer',
                                backgroundColor: field.value === 'uncategorized' ? 'action.selected' : 'transparent',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Iconify icon="eva:file-text-outline" width={16} height={16} sx={{ mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">Uncategorized</Typography>
                            </Box>
                          )}

                          {/* Existing categories */}
                          {filteredCategories.length > 0 && (
                            <>
                              {!hasUncategorizedCategory && <Divider />}
                              {filteredCategories.map((category) => (
                                <Box
                                  key={category._id}
                                  onClick={() => {
                                    field.onChange(category._id);
                                    setOpen(false);
                                    setSearch('');
                                  }}
                                  sx={{
                                    px: 2,
                                    py: 1.5,
                                    cursor: 'pointer',
                                    backgroundColor: field.value === category._id ? 'action.selected' : 'transparent',
                                    '&:hover': {
                                      backgroundColor: 'action.hover',
                                    },
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Iconify icon="eva:folder-outline" width={16} height={16} sx={{ mr: 1, color: 'primary.main' }} />
                                  <Typography variant="body2">{category.name}</Typography>
                                </Box>
                              ))}
                            </>
                          )}

                          {/* Create new category option */}
                          {search.trim() && !exactMatch && onCreateCategory && (
                            <>
                              <Divider />
                              <Box sx={{ p: 1 }}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  disabled={isCreating}
                                  onClick={() => handleCreateCategory(field)}
                                  startIcon={
                                    isCreating ? 
                                      <CircularProgress size={16} /> : 
                                      <Iconify icon="eva:plus-circle-outline" width={16} height={16} />
                                  }
                                  sx={{
                                    borderStyle: 'dashed',
                                    color: 'primary.main',
                                    borderColor: 'primary.main',
                                    '&:hover': {
                                      borderColor: 'primary.main',
                                    }
                                  }}
                                >
                                  {isCreating ? 'Creating...' : `Create "${search.trim()}"`}
                                </Button>
                              </Box>
                            </>
                          )}

                          {/* No results */}
                          {search && filteredCategories.length === 0 && exactMatch && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Category already exists
                              </Typography>
                            </Box>
                          )}

                          {/* Empty state */}
                          {!search && categories.length === 0 && (
                            <Box sx={{ p: 2, textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                No categories available
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </ClickAwayListener>
                  </Paper>
                </Fade>
              )}
            </Popper>

            {/* Hidden native select for form submission */}
            <select
              {...field}
              style={{ display: 'none' }}
            >
              {!hasUncategorizedCategory && <option value="uncategorized">Uncategorized</option>}
              {categories.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
          </Box>
        );
      }}
    />
  );
} 